import { heIL, enUS, plPL } from "@material-ui/core/locale";
import { createTheme } from "@material-ui/core/styles";
import { lightTokens, darkTokens } from "./tokens";

const themeLanguages = {
  en: {
    direction: "ltr",
    translation: enUS,
  },
  he: {
    direction: "rtl",
    translation: heIL,
  },
  pl: {
    direction: "ltr",
    translation: plPL,
  },
};

// Dark-only overrides are nested under `:where(html[data-theme="dark"]) &`. On a theme switch
// MUI swaps in the new style sheets during the re-render but removes the old ones only
// afterwards, so for a moment the previous dark rules still apply. <html data-theme> flips
// synchronously, so this guard stops them matching immediately — needed by the light-mode
// print switch in App.js, whose print snapshot is taken before that cleanup. :where() adds
// zero specificity, so these still sit at MUI's base specificity as before.
const DARK_GUARD = ':where(html[data-theme="dark"]) &';
const darkOnly = (style) => ({ [DARK_GUARD]: style });
const darkOnlyOverrides = (overrides) => Object.fromEntries(
  Object.entries(overrides).map(([component, rules]) => [
    component,
    Object.fromEntries(Object.entries(rules).map(([rule, style]) => [rule, darkOnly(style)])),
  ])
);

export const getTheme = (language, mode = "light") => {
  // console.debug("getTheme", themeLanguages);
  // console.debug("getTheme", language);
  const { direction = "rtl", translation = heIL } =
    themeLanguages[language] || themeLanguages["he"];

  // Use Helvetica for Polish accounts
  const fontFamily = language === "pl" ? ["Helvetica", "Helvetica Neue", "Arial", "sans-serif"] : ["Assistant"];

  const tokens = mode === "dark" ? darkTokens : lightTokens;

  return createTheme(
    {
      direction,
      breakpoints: {
        values: {
          xs: 0,
          sm: 600,
          md: 960,
          sl: 1300,
          lg: 1367,
          xl: 1920,
        },
      },
      palette: {
        // MUI v4 key — this app only mounts a v4 MuiThemeProvider, not a v5 ThemeProvider,
        // so this does not affect v5 (@mui/material) components' theme.palette.mode.
        type: mode,
        primary: {
          main: tokens.accentPrimary,
        },
        // background/text/divider are deliberately NEVER derived from darkTokens, even
        // in dark mode. These are MUI's own defaults — every one of this app's ~230
        // screens that hasn't been migrated to dark mode yet (only the global chrome
        // has: top bar, side nav, dialogs, tooltips, loader) inherits its card
        // background and text color from exactly these keys. Making them mode-aware
        // here silently broke every unmigrated screen: text.primary flipped to a
        // light color while that screen's own (unmigrated, still-white) card
        // background didn't, so labels rendered as invisible light-on-light text
        // app-wide (confirmed via real staging screenshots, not just review). Dark
        // mode's actual visual effect is scoped to theme.custom.* below, which only
        // the specifically-migrated chrome consumes — leave these lightTokens-only
        // until the per-screen migration (PR-3821-3827) actually lands.
        background: {
          default: lightTokens.background,
          paper: lightTokens.surface,
        },
        text: {
          primary: lightTokens.textPrimary,
          secondary: lightTokens.textSecondary,
          disabled: lightTokens.textDisabled,
        },
        divider: lightTokens.divider,
      },
      // Not a standard MUI palette key — a project-specific namespace for tokens that
      // the style factories in src/style/classes/ need but don't fit MUI's own palette
      // shape (e.g. multi-stop gradient colors). Consumed as theme.custom.* by
      // appBarStyles.js/sideMenuStyle.js/drawerStyles.js and friends.
      custom: {
        // Generic migrated-screen content surface — for Phase 4 (PR-3821-3827)
        // screens that opt into dark mode explicitly via theme.custom.*, same as
        // the chrome does. Deliberately NOT read from theme.palette.background/text
        // (those stay frozen light for every screen that hasn't opted in yet — see
        // the comment on palette.background above).
        // Page background — whatsappStyles.js reads it for the chat panes in dark.
        // Missing until 2 Nov, so those panes had no background and were see-through.
        background: tokens.background,
        surface: tokens.surface,
        surfaceElevated: tokens.surfaceElevated,
        border: tokens.border,
        textPrimary: tokens.textPrimary,
        textSecondary: tokens.textSecondary,
        toolbarBackground: tokens.toolbarBackground,
        toolbarText: tokens.toolbarText,
        toolbarTextSecondary: tokens.toolbarTextSecondary,
        toolbarBorder: tokens.toolbarBorder,
        toolbarHoverBg: tokens.toolbarHoverBg,
        toolbarAccentStop1: tokens.toolbarAccentStop1,
        toolbarAccentStop2: tokens.toolbarAccentStop2,
        toolbarAccentStop3: tokens.toolbarAccentStop3,
        toolbarAccentAlt: tokens.toolbarAccentAlt,
        toolbarAccentContrast: tokens.toolbarAccentContrast,
        toolbarAccentShadow: tokens.toolbarAccentShadow,
        toolbarSubmenuBg: tokens.toolbarSubmenuBg,
        toolbarBase: tokens.toolbarBase,
        tooltipSurface: tokens.tooltipSurface,
        tooltipSurfaceText: tokens.tooltipSurfaceText,
        planAccent: tokens.planAccent,
        planAccentLight: tokens.planAccentLight,
        successIcon: tokens.successIcon,
        tooltipPrimaryAccent: tokens.tooltipPrimaryAccent,
        sidebarActiveBg: tokens.sidebarActiveBg,
        sidebarActiveText: tokens.sidebarActiveText,
        accentBlue: tokens.accentBlue,
        accentOutline: tokens.accentOutline,
        brandPrimary: tokens.brandPrimary,
        brandSecondary: tokens.brandSecondary,
        brandLime: tokens.brandLime,
        brandGradient: tokens.brandGradient,
        brandTint: tokens.brandTint,
        borderSubtle: tokens.borderSubtle,
        borderControl: tokens.borderControl,
        statusError: tokens.statusError,
        chartSeries1: tokens.chartSeries1,
        chartSeries2: tokens.chartSeries2,
      },
      overrides: {
        // Bare <Paper> (dashboard summary cards, popovers, etc. with no styling of
        // their own) falls through to this same frozen-light default — the same
        // gotcha already fixed per-component for BaseDialog/MuiAccordion/MuiPopover,
        // generalized here so screens that just drop in a plain Paper don't need an
        // individual fix. `color` cascades to any child Typography that doesn't set
        // its own color (same mechanism that made editorCont's labels work), so this
        // also fixes most nested text — anything with its own hardcoded dark color
        // is a pre-existing, narrower gap, same class of residual risk as any
        // per-file migration would leave.
        MuiPaper: {
          root: {
            ...(mode === 'dark' ? darkOnly({
              backgroundColor: tokens.surface,
              color: tokens.textPrimary,
            }) : {}),
          },
        },
        MuiOutlinedInput: {
          input: {
            "&::placeholder": {
              color: "rgba(0,0,0)",
              fontWeight: 500,
            },
            fontWeight: 500,
            zIndex: 0,
            ...(mode === 'dark' ? darkOnly({ '&::placeholder': { color: tokens.textSecondary, opacity: 1 } }) : {}),
          },
          ...(mode === 'dark' ? {
            notchedOutline: darkOnly({ borderColor: 'rgba(255, 255, 255, 0.23)' }),
          } : {}),
        },
        // Everything below is dark-only: MUI's own defaults read the frozen-light
        // palette (see `palette` above), so bare MUI primitives render black text and
        // icons on dark surfaces. These sit at MUI's base specificity, so any screen's
        // own class still wins — they only fill the gap where nothing else sets a color.
        ...(mode === 'dark' ? darkOnlyOverrides({
          MuiInputBase: {
            root: { color: tokens.textPrimary, '&$disabled': { color: tokens.textDisabled } },
            input: { '&::placeholder': { color: tokens.textSecondary, opacity: 1 } },
          },
          MuiInput: {
            underline: {
              '&:before': { borderBottomColor: 'rgba(255, 255, 255, 0.3)' },
              '&:hover:not($disabled):before': { borderBottomColor: 'rgba(255, 255, 255, 0.6)' },
            },
          },
          MuiFormLabel: { root: { color: tokens.textSecondary, '&$disabled': { color: tokens.textDisabled } } },
          MuiFormHelperText: { root: { color: tokens.textSecondary, '&$disabled': { color: tokens.textDisabled } } },
          MuiFormControlLabel: { label: { color: tokens.textPrimary, '&$disabled': { color: tokens.textDisabled } } },
          MuiSelect: { icon: { color: tokens.textSecondary } },
          MuiCheckbox: { root: { color: tokens.textSecondary } },
          MuiRadio: { root: { color: tokens.textSecondary } },
          MuiIconButton: { root: { color: tokens.textSecondary } },
          MuiSvgIcon: { colorAction: { color: tokens.textSecondary } },
          MuiButton: { root: { color: tokens.textPrimary } },
          MuiTypography: {
            // Brand red text (#FF1744) is ~4.3:1 on raised dark surfaces; the dark accent clears AA.
            colorPrimary: { color: tokens.accentOutline },
            colorTextPrimary: { color: tokens.textPrimary },
            colorTextSecondary: { color: tokens.textSecondary },
          },
          MuiListItemText: { secondary: { color: tokens.textSecondary } },
          MuiListItemIcon: { root: { color: tokens.textSecondary } },
          MuiListSubheader: { root: { color: tokens.textSecondary }, sticky: { backgroundColor: tokens.surfaceElevated } },
          MuiTableCell: {
            root: { borderBottomColor: tokens.border },
            head: { color: tokens.textPrimary },
            body: { color: tokens.textPrimary },
          },
          MuiTablePagination: { root: { color: tokens.textPrimary } },
          MuiTab: {
            textColorPrimary: { color: tokens.textSecondary },
            textColorSecondary: { color: tokens.textSecondary },
          },
          MuiDivider: { root: { backgroundColor: tokens.divider } },
          MuiChip: {
            root: { backgroundColor: tokens.surfaceElevated, color: tokens.textPrimary },
            deleteIcon: { color: tokens.textSecondary },
          },
          MuiSwitch: { track: { backgroundColor: '#ffffff', opacity: 0.3 } },
          MuiStepLabel: {
            label: { color: tokens.textSecondary, '&$active': { color: tokens.textPrimary }, '&$completed': { color: tokens.textPrimary } },
          },
          MuiStepIcon: { root: { color: 'rgba(255, 255, 255, 0.3)' } },
          MuiAutocomplete: { noOptions: { color: tokens.textSecondary }, loading: { color: tokens.textSecondary } },
          // @material-ui/pickers paints its toolbar and month arrows from palette.background,
          // which stays light here, so the date pickers get their dark surfaces explicitly.
          MuiPickersToolbar: { toolbar: { backgroundColor: tokens.surfaceElevated, borderBottom: `1px solid ${tokens.border}` } },
          MuiPickersToolbarText: { toolbarTxt: { color: tokens.textSecondary }, toolbarBtnSelected: { color: tokens.textPrimary } },
          MuiPickersDay: { day: { color: tokens.textPrimary }, dayDisabled: { color: tokens.textDisabled } },
          MuiPickersCalendarHeader: {
            dayLabel: { color: tokens.textSecondary },
            iconButton: { backgroundColor: 'transparent', color: tokens.textPrimary, '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' } },
          },
          MuiPickersYear: { root: { color: tokens.textPrimary }, yearDisabled: { color: tokens.textDisabled } },
          MuiPickersMonth: { root: { color: tokens.textPrimary }, monthDisabled: { color: tokens.textDisabled } },
        }) : {}),
      },
      typography: {
        fontFamily: fontFamily,
        fontStyle: "normal",
        fontSize: 14,
      },
    },
    translation
  );
};
